import { FC, useCallback } from 'react';

import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import { initialValues, options, UserGuideForm } from '../form';

import css from './CreateUserGuideForm.module.css';

import { userGuideService } from 'v2/api/userGuideService';
import { Card } from 'v2/components/card/Card';
import { CardHeader } from 'v2/components/card/cardHeader/CardHeader';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { UserGuideType } from 'v2/constants';
import { handleHttpError } from 'v2/utils';

interface CreateUserGuideFromProps {
  onSuccess: VoidFunction;
}

export const CreateUserGuideFrom: FC<CreateUserGuideFromProps> = ({ onSuccess }) => {
  const { trigger, isMutating } = useSWRMutation('createGuide', userGuideService.createGuide, {
    onSuccess: () => {
      onSuccess();
      toast.success('The guide has been successfully created');
      formik.resetForm();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<UserGuideForm>({
    initialValues,
    onSubmit: (values) => {
      trigger(values);
    },
  });

  const handleChange = useCallback((type: UserGuideType) => {
    formik.setFieldValue('type', type);
  }, []);

  return (
    <Card className={css.createUserGuideFormContainer}>
      <CardHeader title="Add New Guide" icon={Plus} />

      <form className="form" onSubmit={formik.handleSubmit}>
        <TextField
          name="key"
          value={formik.values.key}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Guide Key"
          required
          fullWidth
        />

        <Select value={formik.values.type} options={options} label="Guide Type" onChange={handleChange} required />

        <TextField
          name="reward"
          value={formik.values.reward === 0 ? '' : formik.values.reward}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Reward"
          type="number"
          required
          fullWidth
        />

        <Switch name="isDefault" checked={formik.values.isDefault} onChange={formik.handleChange} label="Default" />

        <Button type="submit" variant="contained" size="large" loading={isMutating}>
          Create
        </Button>
      </form>
    </Card>
  );
};
